import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Check } from 'lucide-react';
import { getLocalizedPlan } from '../data/mock';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations/translations';

const Services = () => {
  const { language } = useLanguage();
  const services = [
    { ...getLocalizedPlan('startCorp', language), slug: 'incorporate' },
    { ...getLocalizedPlan('launchCorp', language), slug: 'full-launch' },
    { ...getLocalizedPlan('runCorp', language), slug: 'corporate-compliance' }
  ];

  const stepKeys = ['step1', 'step2', 'step3', 'step4', 'step5'];

  return (
    <div className="min-h-screen bg-[#fefdf4]">
      {/* Hero */}
      <section className="bg-[#fefdf4] py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">{t(language, 'pages.servicesHero')}</h1>
          <p className="text-xl text-gray-600">
            {t(language, 'pages.servicesSubtitle')}
          </p>
          <p className="text-sm text-gray-500 mt-3">{t(language, 'sections.installmentsAvailable')}</p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <Link
                key={service.name}
                to={`/start?package=${service.slug}`}
                className="block group"
                data-testid={`service-card-link-${service.slug}`}
              >
              <Card className={`border-2 ${index === 1 ? 'border-blue-900' : 'border-gray-200'} hover:shadow-xl group-hover:border-blue-900 transition-all duration-300 relative h-full`} data-testid={`service-card-${service.name.toLowerCase().replace(/\s+/g, '-')}`}>
                {index === 1 && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: 'var(--golden)', color: 'var(--midnight)' }}>
                      {t(language, 'pricing.mostPopular')}
                    </span>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl">{service.name}</CardTitle>
                  <CardDescription className="text-sm text-gray-500 mt-1">
                    {service.subtitle}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {service.features && (
                    <div>
                      {service.includesIntro && (
                        <p className="text-sm text-gray-700 mb-3">{service.includesIntro}</p>
                      )}
                      <ul className="space-y-2">
                        {service.features.map((feature) => (
                          <li key={feature} className="flex items-start space-x-2">
                            <Check className="w-5 h-5 text-blue-900 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-600">{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="pt-6 border-t border-gray-200">
                    {service.type === 'setup + monthly' ? (
                      <>
                        <div className="flex items-baseline space-x-2">
                          <span className="text-3xl font-bold text-gray-900">${service.setupPrice}</span>
                          <span className="text-base font-normal text-gray-500">USD</span>
                          <span className="text-sm font-normal text-gray-500">{t(language, 'pricing.vat')}</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{t(language, 'pricing.upfrontSetup')}</p>
                        <p className="text-2xl font-semibold text-blue-900 mt-3">
                          + ${service.monthlyPrice} <span className="text-sm font-normal text-gray-500">USD</span>{t(language, 'pricing.perMonthSuffix')} <span className="text-xs font-normal text-gray-500">{t(language, 'pricing.vat')}</span>
                        </p>
                        <p className="text-sm text-gray-500">{t(language, 'pricing.monthlyPlan')}</p>
                      </>
                    ) : (
                      <>
                        <div className="flex items-baseline space-x-2">
                          <span className="text-3xl font-bold text-gray-900">${service.price}</span>
                          <span className="text-base font-normal text-gray-500">USD</span>
                          <span className="text-sm font-normal text-gray-500">{t(language, 'pricing.vat')}</span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{t(language, 'pricing.oneTimeFee')}</p>
                      </>
                    )}
                  </div>
                </CardContent>
              </Card>
              </Link>
            ))}

            {/* Special Project Structuring (orientation-only, no fixed price) */}
            <Link
              to="/start?package=special-project-structuring"
              className="block group"
              data-testid="service-card-link-special-project-structuring"
            >
            <Card
              className="border-2 border-gray-200 hover:shadow-xl group-hover:border-blue-900 transition-all duration-300 relative h-full"
              data-testid="service-card-special-project-structuring"
            >
              <CardHeader>
                <CardTitle className="text-2xl">{t(language, 'servicesPage.specialProjectTitle')}</CardTitle>
                <CardDescription className="text-sm text-gray-500 mt-1">
                  {t(language, 'servicesPage.specialProjectSubtitle')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="text-sm text-gray-700 mb-3">
                    {t(language, 'servicesPage.specialProjectDesc')}
                  </p>
                  <ul className="space-y-2">
                    {['specialProjectBullet1', 'specialProjectBullet2', 'specialProjectBullet3'].map((bk) => (
                      <li key={bk} className="flex items-start space-x-2">
                        <Check className="w-5 h-5 text-blue-900 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{t(language, `servicesPage.${bk}`)}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="pt-6 border-t border-gray-200">
                  <p className="text-3xl font-bold text-gray-900">{t(language, 'servicesPage.specialProjectPriceLabel')}</p>
                  <p className="text-sm text-gray-500 mt-1">{t(language, 'servicesPage.specialProjectPriceSub')}</p>
                </div>
              </CardContent>
            </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* What happens next */}
      <section className="py-20 bg-[#f5f2e8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-lg p-8 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
                {t(language, 'servicesPage.whatHappensNext')}
              </h2>
              <div className="space-y-4">
                {stepKeys.map((key, idx) => (
                  <div key={key} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-900 text-white rounded-full flex items-center justify-center font-semibold">
                      {idx + 1}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{t(language, `servicesPage.${key}`)}</h3>
                      <p className="text-gray-600">{t(language, `servicesPage.${key}Desc`)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#fefdf4]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">{t(language, 'servicesPage.readyToStart')}</h2>
          <p className="text-lg text-gray-600 mb-8">
            {t(language, 'servicesPage.readyToStartDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/start">
              <Button size="lg" data-testid="services-begin-incorporation-btn" className="shine-cta w-full sm:w-auto bg-blue-900 hover:bg-blue-800 text-white px-8">
                {t(language, 'nav.startCta')}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
